import React, { useState, useEffect, useRef } from 'react';
import mqtt from 'mqtt';

function ESP32Dashboard() {
  const [sensorData, setSensorData] = useState({
    temp_c: 0,
    hum_pct: 0,
    light_pct: 0,
    activity_energy: 0,
    activity: 0,
    ts: 0
  });
  const [status, setStatus] = useState('Connecting...');
  const [activityHistory, setActivityHistory] = useState([]);
  const [buzzerPattern, setBuzzerPattern] = useState([1, 0, 1]);
  const [customPattern, setCustomPattern] = useState('1,0,1,0,1');
  const clientRef = useRef(null);

  const MQTT_BROKER = 'wss://kidr78home.duckdns.org/mqtt';
  const SENSOR_TOPIC = 'home/esp32s3/sensors/json';
  const BUZZER_TOPIC = 'home/esp32s3/cmd/buzzer';

  useEffect(() => {
    const client = mqtt.connect(MQTT_BROKER);
    clientRef.current = client;

    client.on('connect', () => {
      setStatus('Connected');
      client.subscribe(SENSOR_TOPIC);
    });

    client.on('error', (err) => {
      setStatus('Connection Error');
      console.error('MQTT Error:', err);
    });

    client.on('message', (topic, message) => {
      try {
        const data = JSON.parse(message.toString());
        setSensorData(data);
        
        // Track activity history (last 60 samples)
        setActivityHistory(prev => {
          const newHistory = [...prev, data.activity_energy];
          return newHistory.slice(-60);
        });
      } catch (err) {
        console.error('Error parsing MQTT message:', err);
      }
    });

    return () => {
      client.end();
    };
  }, []);

  const sendBuzzerPattern = (pattern) => {
    if (clientRef.current && clientRef.current.connected) {
      const payload = JSON.stringify({ pattern });
      clientRef.current.publish(BUZZER_TOPIC, payload);
      setBuzzerPattern(pattern);
    }
  };

  const sendCustomPattern = () => {
    try {
      const pattern = customPattern.split(',').map(n => parseInt(n.trim())).filter(n => n === 0 || n === 1);
      if (pattern.length > 0) {
        sendBuzzerPattern(pattern);
      }
    } catch (e) {
      console.error('Invalid pattern');
    }
  };

  // Preset patterns
  const presets = {
    'Short Beep': [1],
    'Double Beep': [1, 0, 1],
    'Triple Beep': [1, 0, 1, 0, 1],
    'Long Beep': [1, 1, 1, 1],
    'SOS': [1, 0, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 0, 1, 0, 1, 0, 1],
    'Alert': [1, 1, 0, 0, 1, 1, 0, 0, 1, 1],
    'Doorbell': [1, 0, 1, 1, 0, 1],
  };

  const getActivityColor = (energy) => {
    if (energy < 2000) return '#2ecc71';
    if (energy < 10000) return '#f1c40f';
    return '#e74c3c';
  };

  const maxActivity = Math.max(...activityHistory, 1000);

  return (
    <div className="esp-page">
      <style>{`
        .esp-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f4c75 0%, #1b262c 50%, #0f4c75 100%);
          padding: 80px 20px 40px;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .esp-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .page-title {
          color: white;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .page-subtitle {
          color: rgba(255, 255, 255, 0.7);
          font-size: 1rem;
        }
        
        .connection-status {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-top: 1rem;
        }
        
        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .card {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .card-title {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 1rem;
          text-align: center;
        }
        
        .sensor-card {
          text-align: center;
        }
        
        .sensor-icon {
          font-size: 3rem;
          margin-bottom: 0.5rem;
        }
        
        .sensor-value {
          font-size: 3rem;
          font-weight: 700;
          color: white;
        }
        
        .sensor-unit {
          font-size: 1.5rem;
          color: rgba(255, 255, 255, 0.6);
          margin-left: 0.25rem;
        }
        
        .sensor-label {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-top: 0.5rem;
        }
        
        .activity-card {
          grid-column: 1 / -1;
        }
        
        .activity-bars {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 3px;
          height: 80px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          padding: 1rem;
        }
        
        .activity-bar {
          flex: 1;
          max-width: 15px;
          border-radius: 2px 2px 0 0;
          transition: height 0.1s ease;
          min-height: 2px;
        }
        
        .activity-value {
          text-align: center;
          margin-top: 1rem;
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
        }
        
        .buzzer-card {
          grid-column: 1 / -1;
        }
        
        .buzzer-presets {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          justify-content: center;
          margin-bottom: 1.5rem;
        }
        
        .preset-btn {
          background: linear-gradient(135deg, #3282b8, #0f4c75);
          color: white;
          border: none;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          min-width: 100px;
        }
        
        .preset-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 15px rgba(50, 130, 184, 0.4);
        }
        
        .preset-btn:active {
          transform: scale(0.98);
        }
        
        .custom-pattern {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .pattern-input {
          flex: 1;
          min-width: 200px;
          max-width: 300px;
          padding: 0.75rem 1rem;
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          background: rgba(0, 0, 0, 0.3);
          color: white;
          font-size: 1rem;
          font-family: monospace;
        }
        
        .pattern-input:focus {
          outline: none;
          border-color: #3282b8;
        }
        
        .send-btn {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s;
        }
        
        .send-btn:hover {
          transform: scale(1.05);
        }
        
        .pattern-preview {
          display: flex;
          gap: 4px;
          justify-content: center;
          margin-top: 1rem;
          flex-wrap: wrap;
        }
        
        .pattern-dot {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        
        .pattern-dot.on {
          background: #e74c3c;
          box-shadow: 0 0 10px rgba(231, 76, 60, 0.5);
        }
        
        .pattern-dot.off {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .pattern-help {
          text-align: center;
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.8rem;
          margin-top: 0.75rem;
        }
        
        @media (max-width: 768px) {
          .esp-page { padding: 70px 15px 30px; }
          .page-title { font-size: 2rem; }
          .dashboard-grid { grid-template-columns: 1fr 1fr; }
          .sensor-value { font-size: 2.5rem; }
          .activity-card, .buzzer-card { grid-column: 1 / -1; }
          .preset-btn { padding: 0.6rem 1rem; font-size: 0.85rem; }
        }
        
        @media (max-width: 480px) {
          .dashboard-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="esp-header">
        <h1 className="page-title">🔌 ESP32-S3 Testbed</h1>
        <p className="page-subtitle">Real-time sensors & buzzer control</p>
        <div 
          className="connection-status"
          style={{ 
            background: status === 'Connected' ? 'rgba(46, 204, 113, 0.2)' : 'rgba(241, 196, 15, 0.2)',
            color: status === 'Connected' ? '#2ecc71' : '#f1c40f'
          }}
        >
          <span className="status-dot" style={{ background: status === 'Connected' ? '#2ecc71' : '#f1c40f' }} />
          {status}
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Temperature */}
        <div className="card sensor-card">
          <div className="sensor-icon">🌡️</div>
          <div className="sensor-value" style={{ color: sensorData.temp_c > 25 ? '#e74c3c' : sensorData.temp_c < 18 ? '#3498db' : '#2ecc71' }}>
            {sensorData.temp_c}<span className="sensor-unit">°C</span>
          </div>
          <div className="sensor-label">Temperature</div>
        </div>

        {/* Humidity */}
        <div className="card sensor-card">
          <div className="sensor-icon">💧</div>
          <div className="sensor-value" style={{ color: '#3498db' }}>
            {sensorData.hum_pct}<span className="sensor-unit">%</span>
          </div>
          <div className="sensor-label">Humidity</div>
        </div>

        {/* Light */}
        <div className="card sensor-card">
          <div className="sensor-icon">{sensorData.light_pct > 50 ? '☀️' : '🌙'}</div>
          <div className="sensor-value" style={{ color: '#f1c40f' }}>
            {sensorData.light_pct}<span className="sensor-unit">%</span>
          </div>
          <div className="sensor-label">Light Level</div>
        </div>

        {/* Activity Status */}
        <div className="card sensor-card">
          <div className="sensor-icon">{sensorData.activity ? '🏃' : '😴'}</div>
          <div className="sensor-value" style={{ color: sensorData.activity ? '#2ecc71' : '#95a5a6' }}>
            {sensorData.activity ? 'Active' : 'Idle'}
          </div>
          <div className="sensor-label">Motion Status</div>
        </div>

        {/* Activity Energy History */}
        <div className="card activity-card">
          <div className="card-title">Activity Energy (Last 60s)</div>
          <div className="activity-bars">
            {activityHistory.map((val, i) => (
              <div
                key={i}
                className="activity-bar"
                style={{
                  height: `${Math.max(2, (val / maxActivity) * 100)}%`,
                  background: getActivityColor(val)
                }}
              />
            ))}
          </div>
          <div className="activity-value" style={{ color: getActivityColor(sensorData.activity_energy) }}>
            {sensorData.activity_energy.toLocaleString()}
          </div>
        </div>

        {/* Buzzer Control */}
        <div className="card buzzer-card">
          <div className="card-title">🔔 Buzzer Control</div>
          <div className="buzzer-presets">
            {Object.entries(presets).map(([name, pattern]) => (
              <button
                key={name}
                className="preset-btn"
                onClick={() => sendBuzzerPattern(pattern)}
              >
                {name}
              </button>
            ))}
          </div>
          
          <div className="custom-pattern">
            <input
              type="text"
              className="pattern-input"
              value={customPattern}
              onChange={(e) => setCustomPattern(e.target.value)}
              placeholder="1,0,1,0,1"
            />
            <button className="send-btn" onClick={sendCustomPattern}>
              Send Pattern
            </button>
          </div>
          
          <div className="pattern-preview">
            {buzzerPattern.map((val, i) => (
              <div key={i} className={`pattern-dot ${val ? 'on' : 'off'}`} />
            ))}
          </div>
          <div className="pattern-help">
            Pattern: 1 = buzz, 0 = silence (each ~0.2s)
          </div>
        </div>
      </div>
    </div>
  );
}

export default ESP32Dashboard;
